/**
 * Event landing-page template — 07-press ("The Press Chronicle").
 *
 * Distinct CraftJS home-page document for the `press` theme. Each event
 * template owns its full content/layout. Authored from the design spec in
 *   TEMPLATES - LANDINPAGES/07-press/sections/*.html (CRAFT_PROPS_HINT blocks).
 *
 * Visual style — editorial-briefing: monochrome editorial / newsprint,
 * near-black ink on warm-white, red press accent, tight sans body + serif
 * headlines, hard rounding (rounded-none).
 *
 * Convention (mirrors webinar.ts):
 *  - Section-level marketing copy (headlines, paragraphs, button labels) goes
 *    through td() keys (`prs*` prefix) → localized.
 *  - Repetitive structured child data (agenda rows, speakers, FAQ, …) also
 *    go through td() keys (Wave 3b i18n) — only fictitious proper names,
 *    times-free figures and assets stay inline.
 *  - Global colors/fonts come from PRESS_PRESET (pressPreset); only sections
 *    with their own surface (Hero/Testimonials/Countdown) set explicit colors.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import { direction } from "@/lib/designDirections/directions";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Press-specific asset namespace (seeded under <CDN>/press/…)
const W = `${A}/shared/templates/07-press`;

// WS4-B2: press palette is registry-synced — the values are byte-equal to the
// shared design-directions canon, so the consts read straight from it.
const ACCENT = direction("press").primary; // press red (#C00000)
const INK = direction("press").text; // near-black ink (#1A1A1A)
const MUTE = "#4a4a4a"; // muted body
const MUTE2 = "#6b6b6b"; // faint caption
const CREAM = direction("press").background; // warm-white surface (#FFFCF7)
const DARK = direction("press").text; // dark section surface (#1A1A1A)
const ON_DARK = "#d4d4d4"; // body text on dark
const ON_DARK_ACCENT = "#ff6b6b"; // eyebrow on dark
// WS4-B2: headlines follow the direction canon ("Playfair Display", self-hosted
// below); Georgia stays the newsprint body face (gelasio = the PDF-parity face).
const DISPLAY = "'Playfair Display', Georgia, 'Times New Roman', serif";
const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "Inter, 'Helvetica Neue', Arial, sans-serif";

// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `<family>/*`
// art stays what it is: heroes, details and gallery photography.
const LOGO = `${A}/shared/logo.svg`;

// ── Child data (localized via td — Wave 3b i18n) ──────────────────────
// Structured child rows (numbers, agenda, speakers, testimonials, blog, FAQ)
// come from `prs*` td keys so they follow the site's primaryLanguage. Only
// fictitious proper names and bare figures stay inline.

const numbersData = (td: TranslationFn) => [
  { num: "<b>412</b>", desc: td("prsNum1Desc") },
  { num: "<b>87 M</b>", desc: td("prsNum2Desc") },
  { num: "<b>24</b>", desc: td("prsNum3Desc") },
  { num: "<b>1.146</b>", desc: td("prsNum4Desc") },
];

const agendaData = (td: TranslationFn) => [
  { time: `<b>${td("prsAgendaRow1Time")}</b>`, title: td("prsAgendaRow1Title"), speaker: td("prsAgendaRow1Who"), desc: td("prsAgendaRow1Desc") },
  { time: `<b>${td("prsAgendaRow2Time")}</b>`, title: td("prsAgendaRow2Title"), speaker: td("prsAgendaRow2Who"), desc: td("prsAgendaRow2Desc") },
  { time: `<b>${td("prsAgendaRow3Time")}</b>`, title: td("prsAgendaRow3Title"), speaker: td("prsAgendaRow3Who"), desc: td("prsAgendaRow3Desc") },
  { time: `<b>${td("prsAgendaRow4Time")}</b>`, title: td("prsAgendaRow4Title"), speaker: td("prsAgendaRow4Who"), desc: td("prsAgendaRow4Desc") },
  { time: `<b>${td("prsAgendaRow5Time")}</b>`, title: td("prsAgendaRow5Title"), speaker: td("prsAgendaRow5Who"), desc: td("prsAgendaRow5Desc") },
];

const speakerData = (td: TranslationFn) => [
  { name: "Dr. Helena Brandt", role: td("prsSpeaker1Role") },
  { name: "Markus Vogel", role: td("prsSpeaker2Role") },
  { name: "Sophie Reiter", role: td("prsSpeaker3Role") },
  { name: "Jonas Keller", role: td("prsSpeaker4Role") },
  { name: "Aylin Kaya", role: td("prsSpeaker5Role") },
  { name: "Thomas Berger", role: td("prsSpeaker6Role") },
];

const testimonialData = (td: TranslationFn) => [
  { quote: td("prsTesti1Quote"), name: "Clara Wenzel", role: td("prsTesti1Role") },
  { quote: td("prsTesti2Quote"), name: "Daniel Frei", role: td("prsTesti2Role") },
  { quote: td("prsTesti3Quote"), name: "Nadia Hofer", role: td("prsTesti3Role") },
];

const blogData = (td: TranslationFn) => [
  { topic: `<b>${td("prsBlog1Topic")}</b>`, content: td("prsBlog1Content") },
  { topic: `<b>${td("prsBlog2Topic")}</b>`, content: td("prsBlog2Content") },
  { topic: `<b>${td("prsBlog3Topic")}</b>`, content: td("prsBlog3Content") },
];

const faqData = (td: TranslationFn) => [
  { q: `<b>${td("prsFaq1Question")}</b>`, a: td("prsFaq1Answer") },
  { q: `<b>${td("prsFaq2Question")}</b>`, a: td("prsFaq2Answer") },
  { q: `<b>${td("prsFaq3Question")}</b>`, a: td("prsFaq3Answer") },
  { q: `<b>${td("prsFaq4Question")}</b>`, a: td("prsFaq4Answer") },
  { q: `<b>${td("prsFaq5Question")}</b>`, a: td("prsFaq5Answer") },
  { q: `<b>${td("prsFaq6Question")}</b>`, a: td("prsFaq6Answer") },
];

// ── Press-specific child factories ────────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = numbersData(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = agendaData(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.speaker, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = speakerData(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = testimonialData(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left", fontColorContent: ON_DARK,
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left", fontColorName: "#ffffff",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left", fontColorOccupation: ON_DARK,
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = blogData(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = faqData(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/hero-einladung.jpg`, `${W}/detail-microphones.jpg`, `${W}/detail-paper.jpg`,
  `${W}/detail-press-folder.jpg`, `${W}/hero-zusage.jpg`, `${W}/hero-dokumente.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const press = (td: TranslationFn): string => {
  const B = "prs_body";

  const HERO = "prs_hero";
  const NUMBERS = "prs_numbers";
  const AGENDA = "prs_agenda";
  const SPEAKER = "prs_speaker";
  const TEXT = "prs_text";
  const IMAGE = "prs_image";
  const COUNTDOWN = "prs_countdown";
  const GALLERY = "prs_gallery";
  const VIDEO = "prs_video";
  const TESTI = "prs_testimonials";
  const BLOG = "prs_blog";
  const FAQ = "prs_faq";
  const MAP = "prs_map";
  const DI = "prs_datainput";
  const CONTACT = "prs_contact";

  return enc({
    ...scaffold(
      { ab: "prs_ab", abs: "prs_abs", body: B, ft: "prs_ft", fts: "prs_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-none" },
    ),

    // ── Hero (image right, dark ink on warm-white) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 90, contentAlignment: "center", imageLayout: "right", layoutWidth: "50%", inversed: true,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("prsHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: ACCENT,
        isVisibleHeader: true, textHeader: td("prsHeroHeader"), fontSizeHeader: "text-5xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("prsHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("prsHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("prsHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "image", imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: CREAM,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "prs_hero_eb", header: "prs_hero_hd", text: "prs_hero_tx", buttonOne: "prs_hero_b1", buttonTwo: "prs_hero_b2" },
    },
    prs_hero_eb: tc(HERO, { isVisible: true, text: td("prsHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    prs_hero_hd: tc(HERO, { isVisible: true, text: td("prsHeroHeader"), fontSize: "text-5xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    prs_hero_tx: tc(HERO, { isVisible: true, text: td("prsHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    prs_hero_b1: btn(HERO, { type: "secondary", text: td("prsHeroSecondaryButton"), displayName: "buttonOne" }),
    prs_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("prsHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers (press distribution) ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("prsNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("prsNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["prs_num1", "prs_num2", "prs_num3", "prs_num4"],
      linkedNodes: { headline: "prs_num_eb", header: "prs_num_hd", text: "prs_num_tx" },
    },
    prs_num_eb: tc(NUMBERS, { isVisible: false, text: td("prsNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    prs_num_hd: tc(NUMBERS, { isVisible: true, text: td("prsNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    prs_num_tx: tc(NUMBERS, { isVisible: true, text: td("prsNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    prs_num1: numbersChild(NUMBERS, 0, td),
    prs_num2: numbersChild(NUMBERS, 1, td),
    prs_num3: numbersChild(NUMBERS, 2, td),
    prs_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda (briefing schedule) ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("prsAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("prsAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["prs_ag1", "prs_ag2", "prs_ag3", "prs_ag4", "prs_ag5"],
      linkedNodes: { headline: "prs_ag_eb", header: "prs_ag_hd", text: "prs_ag_tx" },
    },
    prs_ag_eb: tc(AGENDA, { isVisible: false, text: td("prsAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    prs_ag_hd: tc(AGENDA, { isVisible: true, text: td("prsAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    prs_ag_tx: tc(AGENDA, { isVisible: true, text: td("prsAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    prs_ag1: agendaChild(AGENDA, 0, td),
    prs_ag2: agendaChild(AGENDA, 1, td),
    prs_ag3: agendaChild(AGENDA, 2, td),
    prs_ag4: agendaChild(AGENDA, 3, td),
    prs_ag5: agendaChild(AGENDA, 4, td),

    // ── Speaker (spokespersons & experts) — hidden by default (no portraits) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("prsSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("prsSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["prs_sp1", "prs_sp2", "prs_sp3", "prs_sp4", "prs_sp5", "prs_sp6"],
      linkedNodes: { headline: "prs_sp_eb", header: "prs_sp_hd", text: "prs_sp_tx" },
    },
    prs_sp_eb: tc(SPEAKER, { isVisible: false, text: td("prsSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    prs_sp_hd: tc(SPEAKER, { isVisible: true, text: td("prsSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    prs_sp_tx: tc(SPEAKER, { isVisible: true, text: td("prsSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    prs_sp1: speakerChild(SPEAKER, 0, td),
    prs_sp2: speakerChild(SPEAKER, 1, td),
    prs_sp3: speakerChild(SPEAKER, 2, td),
    prs_sp4: speakerChild(SPEAKER, 3, td),
    prs_sp5: speakerChild(SPEAKER, 4, td),
    prs_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (editorial — why this briefing exists) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("prsTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("prsTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("prsTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("prsTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "prs_tx_eb", header: "prs_tx_hd", text: "prs_tx_tx", buttonOne: "prs_tx_b1", buttonTwo: "prs_tx_b2" },
    },
    prs_tx_eb: tc(TEXT, { isVisible: false, text: td("prsTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    prs_tx_hd: tc(TEXT, { isVisible: true, text: td("prsTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", header: true }),
    prs_tx_tx: tc(TEXT, { isVisible: true, text: td("prsTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    prs_tx_b1: btn(TEXT, { type: "secondary", text: td("prsTextSecondaryButton"), displayName: "buttonOne" }),
    prs_tx_b2: btn(TEXT, { type: "primary", text: td("prsTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (press-kit contents, image left / text right) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("prsImageHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("prsImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("prsImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("prsImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-none", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-microphones.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "prs_im_eb", header: "prs_im_hd", text: "prs_im_tx", button: "prs_im_btn", image: "prs_im_img" },
    },
    prs_im_eb: tc(IMAGE, { isVisible: false, text: td("prsImageHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    prs_im_hd: tc(IMAGE, { isVisible: true, text: td("prsImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    prs_im_tx: tc(IMAGE, { isVisible: true, text: td("prsImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    prs_im_btn: btn(IMAGE, { text: td("prsImageButton"), displayName: "button" }),
    prs_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-microphones.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown (embargo) ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("prsCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("prsCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("prsCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("prsCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: SERIF, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: ACCENT, countdownBorderColor: INK,
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "prs_cd_eb", header: "prs_cd_hd", text: "prs_cd_tx", button: "prs_cd_btn" },
    },
    prs_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("prsCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    prs_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("prsCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    prs_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("prsCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    prs_cd_btn: btn(COUNTDOWN, { text: td("prsCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (press photos) — hidden by default (no photo library) ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "4", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("prsGalleryHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("prsGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("prsGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("prsGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["prs_g1", "prs_g2", "prs_g3", "prs_g4", "prs_g5", "prs_g6"],
      linkedNodes: { headline: "prs_g_eb", header: "prs_g_hd", text: "prs_g_tx", secondary: "prs_g_b2", primary: "prs_g_b1" },
    },
    prs_g_eb: tc(GALLERY, { isVisible: false, text: td("prsGalleryHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    prs_g_hd: tc(GALLERY, { isVisible: true, text: td("prsGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    prs_g_tx: tc(GALLERY, { isVisible: false, text: td("prsGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    prs_g_b2: btn(GALLERY, { type: "secondary", text: td("prsGallerySecondaryButton"), displayName: "ButtonChild" }),
    prs_g_b1: btn(GALLERY, { text: td("prsGalleryPrimaryButton"), displayName: "ButtonChild" }),
    prs_g1: galleryImage(GALLERY, 0),
    prs_g2: galleryImage(GALLERY, 1),
    prs_g3: galleryImage(GALLERY, 2),
    prs_g4: galleryImage(GALLERY, 3),
    prs_g5: galleryImage(GALLERY, 4),
    prs_g6: galleryImage(GALLERY, 5),

    // ── Video (recap) — present but hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("prsVideoHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("prsVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("prsVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "prs_vd_eb", header: "prs_vd_hd", text: "prs_vd_tx", button: "prs_vd_btn" },
    },
    prs_vd_eb: tc(VIDEO, { isVisible: false, text: td("prsVideoHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    prs_vd_hd: tc(VIDEO, { isVisible: true, text: td("prsVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    prs_vd_tx: tc(VIDEO, { isVisible: true, text: td("prsVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    prs_vd_btn: btn(VIDEO, { text: td("prsVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (correspondent pool — dark surface) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: DARK,
        isVisibleHeadline: true, textHeadline: td("prsTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("prsTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["prs_ts1", "prs_ts2", "prs_ts3"],
      linkedNodes: { headline: "prs_ts_eb", header: "prs_ts_hd", text: "prs_ts_tx" },
    },
    prs_ts_eb: tc(TESTI, { isVisible: true, text: td("prsTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ON_DARK_ACCENT, header: true }),
    prs_ts_hd: tc(TESTI, { isVisible: true, text: td("prsTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: "#ffffff", header: true }),
    prs_ts_tx: tc(TESTI, { isVisible: false, text: td("prsTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    prs_ts1: testimonialChild(TESTI, 0, td),
    prs_ts2: testimonialChild(TESTI, 1, td),
    prs_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (press releases & backgrounders) — present but hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("prsBlogHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("prsBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["prs_bl1", "prs_bl2", "prs_bl3"],
      linkedNodes: { headline: "prs_bl_eb", header: "prs_bl_hd", text: "prs_bl_tx" },
    },
    prs_bl_eb: tc(BLOG, { isVisible: false, text: td("prsBlogHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    prs_bl_hd: tc(BLOG, { isVisible: true, text: td("prsBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    prs_bl_tx: tc(BLOG, { isVisible: true, text: td("prsBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    prs_bl1: blogChild(BLOG, 0, td),
    prs_bl2: blogChild(BLOG, 1, td),
    prs_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("prsFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("prsFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["prs_fq1", "prs_fq2", "prs_fq3", "prs_fq4", "prs_fq5", "prs_fq6"],
      linkedNodes: { headline: "prs_fq_eb", header: "prs_fq_hd", text: "prs_fq_tx" },
    },
    prs_fq_eb: tc(FAQ, { isVisible: false, text: td("prsFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    prs_fq_hd: tc(FAQ, { isVisible: true, text: td("prsFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    prs_fq_tx: tc(FAQ, { isVisible: true, text: td("prsFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    prs_fq1: faqChild(FAQ, 0, td),
    prs_fq2: faqChild(FAQ, 1, td),
    prs_fq3: faqChild(FAQ, 2, td),
    prs_fq4: faqChild(FAQ, 3, td),
    prs_fq5: faqChild(FAQ, 4, td),
    prs_fq6: faqChild(FAQ, 5, td),

    // ── Map (briefing venue & press zone) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("prsMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("prsMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("prsMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("prsMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-none",
        mapZoom: 12, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("prsMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "prs_mp_eb", header: "prs_mp_hd", text: "prs_mp_tx", button: "prs_mp_btn" },
    },
    prs_mp_eb: tc(MAP, { isVisible: false, text: td("prsMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    prs_mp_hd: tc(MAP, { isVisible: true, text: td("prsMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    prs_mp_tx: tc(MAP, { isVisible: true, text: td("prsMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    prs_mp_btn: btn(MAP, { text: td("prsMapButton"), displayName: "button" }),

    // ── DataInput (multi-step accreditation) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("prsDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("prsDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("prsDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("prsStepStatus"), td("prsStepOutlet"), td("prsStepEquipment"), td("prsStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: ACCENT,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "prs_di_hl", header: "prs_di_hd", text: "prs_di_tx",
        fieldsGroup0: "prs_di_f0", fieldsGroup1: "prs_di_f1", fieldsGroup2: "prs_di_f2",
        fieldsGroup3: "prs_di_f3", fieldsGroup4: "prs_di_f4",
        companionsGroup: "prs_di_comp", overviewGroup: "prs_di_ov", checkboxes: "prs_di_cb",
        button: "prs_di_btn", backButton: "prs_di_back", nextButton: "prs_di_next",
      },
    },
    prs_di_hl: tc(DI, { isVisible: true, text: td("prsDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    prs_di_hd: tc(DI, { isVisible: true, text: td("prsDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    prs_di_tx: tc(DI, { isVisible: true, text: td("prsDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    prs_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["prs_di_acceptance", "prs_di_first", "prs_di_last", "prs_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    prs_di_acceptance: acceptanceFi("prs_di_f0", td("acceptanceState")),
    prs_di_first: fi("prs_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    prs_di_last: fi("prs_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    prs_di_email: fi("prs_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    prs_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["prs_di_outlet", "prs_di_press"], linkedNodes: {},
    },
    prs_di_outlet: fi("prs_di_f1", { label: td("prsFieldOutlet"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "outlet", changeable: true }),
    prs_di_press: fi("prs_di_f1", { label: td("prsFieldPressId"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "pressId", changeable: true }),
    prs_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["prs_di_equipment"], linkedNodes: {},
    },
    prs_di_equipment: fi("prs_di_f2", { label: td("prsFieldEquipment"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "equipment", multiline: true, gridArea: "col-span-12", changeable: true }),
    prs_di_f3: emptyFieldsGroup(DI, 3),
    prs_di_f4: emptyFieldsGroup(DI, 4),
    prs_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["prs_di_comp_first"], linkedNodes: {},
    },
    prs_di_comp_first: fi("prs_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    prs_di_ov_t: tc("prs_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    prs_di_ov_x: tc("prs_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    prs_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["prs_di_ov_t", "prs_di_ov_x"], linkedNodes: {},
    },
    prs_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    prs_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("prsDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    prs_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    prs_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact (press office) ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("prsContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("prsContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("prsContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "twitter", url: "https://twitter.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("prsContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "prs_ct_eb", header: "prs_ct_hd", text: "prs_ct_tx",
        firstName: "prs_ct_first", lastName: "prs_ct_last", eMail: "prs_ct_email",
        phoneNumber: "prs_ct_phone", Message: "prs_ct_msg", button: "prs_ct_btn",
      },
    },
    prs_ct_eb: tc(CONTACT, { isVisible: true, text: td("prsContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    prs_ct_hd: tc(CONTACT, { isVisible: true, text: td("prsContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    prs_ct_tx: tc(CONTACT, { isVisible: true, text: td("prsContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    prs_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    prs_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    prs_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    prs_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    prs_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    prs_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("prsContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Warm-white bar, near-black rule, logo + nav + "Akkreditieren" CTA. */
export const pressAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      // WS4-B2: deliberately NOT transparent — the hero is warm-white newsprint,
      // so a see-through bar would sit light-on-light with no readable edge.
      backgroundColor: CREAM,
      border: true, borderColor: INK, borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("prsLinkBriefing"), url: "#prs_agenda", linkType: "internal" },
        { label: td("prsLinkSpeakers"), url: "#prs_speaker", linkType: "internal" },
        { label: td("prsLinkAccredit"), url: "#prs_datainput", linkType: "internal" },
        { label: td("prsLinkPressOffice"), url: "#prs_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("prsHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: INK, logoutButtonColor: "#ffffff", logoutButtonBorderColor: INK,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Dark footer, red top rule, four link groups + social + copyright. */
export const pressFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: DARK,
      // border-4 + borderSides:["top"] → getBorderClasses emits "border-t-4".
      border: true, borderColor: ACCENT, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      // No footer logo (per design) — omit "logo" from sectionOrder + hide it.
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("prsFooterProgram"), links: [
          { label: td("prsLinkBriefing"), url: "#prs_agenda", linkType: "internal" },
          { label: td("prsLinkSpeakers"), url: "#prs_speaker", linkType: "internal" },
          { label: td("prsLinkReleases"), url: "#prs_blog", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (new tab).
        { title: td("prsFooterEvent"), links: [
          { label: td("prsLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Berlin", linkType: "external" },
          { label: td("prsLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Pressekonferenz+Vol.+XII", linkType: "external" },
          { label: td("prsLinkAccredit"), url: "#prs_datainput", linkType: "internal" },
        ] },
        { title: td("prsFooterSupport"), links: [
          { label: td("prsLinkPressOffice"), url: "#prs_contact", linkType: "internal" },
          { label: td("prsLinkFaq"), url: "#prs_faq", linkType: "internal" },
          { label: td("prsLinkPressKit"), url: "#prs_image", linkType: "internal" },
        ] },
        { title: td("prsFooterLegal"), links: [
          { label: td("prsLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("prsLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("prsLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#ffffff", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#a3a3a3", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "twitter", platform: "twitter", url: "https://twitter.com" },
      ],
      socialColor: "#a3a3a3", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#6b6b6b", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Theme CSS (P9 WS4-B2) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero keeps its light split layout (photo right, node props untouched);
// the css layers the Chronicle masthead treatment underneath: a stark ink
// double rule across the top of the page (the newspaper masthead), an ink
// hairline under the fold, uppercase letterspaced kickers, and the
// self-hosted faces so "Playfair Display"/Georgia resolve identically
// everywhere (playfair-display + gelasio, the PDF-parity faces).
const CUSTOM_CSS = `/* theme:press */
${fontFaces([
  { family: "Playfair Display", file: "playfair-display", weightRange: "400 900" },
  { family: "Georgia", file: "gelasio-regular", weightRange: "400" },
  { family: "Georgia", file: "gelasio-bold", weightRange: "700" },
])}

/* Chronicle hero — newsprint sheet (node bg stays), clipped for the rules */
#prs_hero {
${heroShell}
}

/* Masthead: the classic double rule — thick over thin ink lines (draws itself
   on entrance) */
#prs_hero::before {
${decorLayer}
  top: 0;
  left: 4%;
  right: 4%;
  height: 7px;
  border-top: 3px solid ${INK};
  border-bottom: 1px solid ${INK};
}

/* End rule under the fold — stark, full column measure */
#prs_hero::after {
${decorLayer}
  left: 4%;
  right: 4%;
  bottom: 7%;
  height: 1px;
  background: ${INK};
  opacity: 0.35;
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#prs_hero .text-xs, #prs_numbers .text-xs, #prs_agenda .text-xs, #prs_speaker .text-xs,
#prs_text .text-xs, #prs_image .text-xs, #prs_countdown .text-xs, #prs_gallery .text-xs,
#prs_video .text-xs, #prs_testimonials .text-xs, #prs_blog .text-xs, #prs_faq .text-xs,
#prs_map .text-xs, #prs_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("prs", 14)}
  @keyframes prsRuleGrow {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }

  /* Edition entrance: the page settles, the masthead rules print themselves */
  #prs_hero { animation: prsFadeUp 0.6s ${EASE} both; }
  #prs_hero::before {
    transform-origin: center;
    animation: prsRuleGrow 1.1s ${EASE} 0.3s both;
  }

${revealBlock("prs", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset ──────────────────────────────────────────────────────
// editorial-briefing: monochrome editorial / newsprint, near-black ink on
// warm-white, red press accent, Georgia newsprint body + Playfair masthead
// headlines (direction canon), rounded-none.
export const pressPreset: WebsiteThemeStylePreset = {
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: ACCENT, secondary: INK, background: CREAM },
  fonts: { header: DISPLAY, body: SERIF },
  rounding: "rounded-none",
  recommendedSize: "medium",
  bodyTheme: { fontColor: INK, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    // WS4-B2 display bump: 44px h1, 26px h2 (Playfair masthead register).
    h1: { fontFamily: DISPLAY, fontSize: "44px", fontColor: INK },
    h2: { fontFamily: DISPLAY, fontSize: "26px", fontColor: INK },
  },
  buttonsTheme: {
    // Primary stays SOLID ink (white label 17.40:1; red hover 6.48:1);
    // secondary is a true ghost (transparent, ink hairline) — WS4-B2 pairing.
    primary: { bgColor: INK, bgHoverColor: ACCENT, borderColor: INK, borderHoverColor: ACCENT, fontColor: "#ffffff", fontHoverColor: "#ffffff", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-none", hoverBorderRadius: "rounded-none" },
    secondary: { bgColor: "transparent", bgHoverColor: INK, borderColor: INK, borderHoverColor: INK, fontColor: INK, fontHoverColor: "#ffffff", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-none", hoverBorderRadius: "rounded-none" },
  },
  hyperlinkTheme: { fontColor: ACCENT, fontHoverColor: "#8b0000", underline: false },
  formInputTheme: { labelFontColor: INK, inputFontColor: INK, backgroundColor: "#ffffff", border: true, borderColor: INK, borderRadius: "rounded-none", borderStyle: "border-solid", borderWidth: "border", borderSides: [], fontFamily: SANS, fontSize: "text-sm", errorColor: "#DC2626" },
  customCss: CUSTOM_CSS,
};
